<script setup lang="ts">
import { computed, onMounted, ref, type Ref } from 'vue';
import UserAccountInfo from '@/components/UserAccountInfo.vue';
import { useAuthUser } from '@/helpers/globals';
import { createValidator, createValidators, validateEmail } from '@kp/geofy-vuejs-lib/tools/model_validators.js';
const authUser = useAuthUser();
console.log(JSON.parse(JSON.stringify(authUser)));
import { instance as api } from '@/helpers/api';
import { errToStr } from '@/helpers/tools';

const apierror: Ref<string> = ref('');
const emailerror: Ref<string> = ref('');
const active_role_stelle_eplan_email_address: Ref<string> = ref('');

const { validators } = createValidators({
    active_role_stelle_eplan_email_address: createValidator(
        computed(() => active_role_stelle_eplan_email_address.value),
        [validateEmail],
        { initialValidate: false }
    )
});

onMounted(() => {
    active_role_stelle_eplan_email_address.value = authUser?.active_role_stelle_eplan_email_address || '';
});

async function togglePersonalEmailNotification(isOn: boolean) {
    const stelle_id = authUser?.get('active_role_stelle_id');
    apierror.value = '';
    try {
        await api().post('/User/setGemeindePersonalNotification', {
            stelle_id,
            isOn
        });
        authUser?.set('active_user_stelle_personal_event_notification_per_mail', isOn);
    } catch (err) {
        console.error(errToStr(err));
        apierror.value = errToStr(err);
        return {};
    }
}

async function toggleGemeindeEmailNotification(isOn: boolean) {
    const stelle_id = authUser?.get('active_role_stelle_id');
    apierror.value = '';
    try {
        await api().post('/User/setGemeindeNotification', {
            stelle_id,
            isOn
        });
        authUser?.set('active_role_stelle_event_notification_per_mail', isOn);
    } catch (err) {
        console.error(errToStr(err));
        apierror.value = errToStr(err);
        return {};
    }
}

async function setStelleEplanEmail(val: string) {
    active_role_stelle_eplan_email_address.value = val;
    validators.active_role_stelle_eplan_email_address.validate();
    if (validators.active_role_stelle_eplan_email_address.isValid.value) {
        try {
            const stelle_id = authUser?.get('active_role_stelle_id');
            await api().post('/User/setGemeindeEplanEmail', {
                stelle_id,
                email: val
            });
            authUser?.set('active_role_stelle_eplan_email_address', val);
        } catch (err) {
            console.error(errToStr(err));
            emailerror.value = errToStr(err);
            return {};
        }
    }
}
</script>

<template>
    <div class="flex gap-5rem mb-5rem">
        <div class="left flex-grow-1">
            <h2 class="ktg-title-02 mb-2rem">{{ $t('notifications') }}</h2>
            <p class="ktg-body-small mt-1rem mb-1rem" v-html="$t('useraccount_notifications_gemeinde_info_text')"></p>
            <div class="useraccountprofilecontainer p-3rem">
                <h5 class="ktg-title-05">{{ $t('email_benachrichtigungen') }}</h5>
                <div class="ktg-title-06 mt-2rem">{{ $t('perseoenliche_benachrichtigungen') }}</div>
                <div class="element mt-1rem p-1_5rem">
                    <ktg-labelled-choice>
                        <ktg-toggle
                            name="active_user_stelle_personal_event_notification_per_mail"
                            input-id="active_user_stelle_personal_event_notification_per_mail"
                            :checked="authUser?.active_user_stelle_personal_event_notification_per_mail"
                            @change="togglePersonalEmailNotification($event.target?.checked)"
                        />
                        <ktg-label for="active_user_stelle_personal_event_notification_per_mail">{{
                            $t('event_notification_per_mail_persoenlich').replace(
                                '%GEMEINDE%',
                                authUser?.get('active_role_stelle')
                            )
                        }}</ktg-label>
                    </ktg-labelled-choice>
                </div>

                <div class="ktg-title-06 mt-2rem mb-1rem">{{ $t('gemeindeweite_benachrichtigungen') }}</div>
                <ktg-clues>
                    <ktg-clue>
                        <ktg-clue-title icon="info">{{
                            $t('gemeindeweite_benachrichtigungen_info').replace(
                                '%GEMEINDE%',
                                authUser?.get('active_role_stelle')
                            )
                        }}</ktg-clue-title>
                    </ktg-clue>
                </ktg-clues>
                <div class="element mt-1rem p-1_5rem">
                    <ktg-labelled-choice>
                        <ktg-toggle
                            name="event_notification_per_mail_gemeinde"
                            input-id="event_notification_per_mail_gemeinde"
                            :checked="authUser?.active_role_stelle_event_notification_per_mail"
                            @change="toggleGemeindeEmailNotification($event.target?.checked)"
                        />
                        <ktg-label for="event_notification_per_mail_gemeinde">{{
                            $t('event_notification_per_mail_gemeinde')
                        }}</ktg-label>
                    </ktg-labelled-choice>
                </div>
                <ktg-clues class="mt-1rem" appearance="danger" v-if="apierror !== ''">
                    <ktg-clue>
                        <ktg-clue-title icon="warning">{{ apierror }}</ktg-clue-title>
                    </ktg-clue>
                </ktg-clues>

                <ktg-field class="mt-2rem" v-if="authUser?.active_role_stelle_event_notification_per_mail">
                    <ktg-label for="eplan_email_address">{{ $t('bitte_gemeindepostfach_angeben') }}:</ktg-label>
                    <ktg-text-input
                        class="w-full"
                        :value="active_role_stelle_eplan_email_address"
                        input-id="active_role_stelle_eplan_email_address"
                        type="text"
                        :placeholder="$t('gemeinde_email_placeholder')"
                        error-id="err_active_role_stelle_eplan_email_address"
                        :error="!validators.active_role_stelle_eplan_email_address.isValid.value"
                        @blur="setStelleEplanEmail($event.target?.value)"
                    />
                    <ktg-field-error
                        v-if="!validators.active_role_stelle_eplan_email_address.isValid.value"
                        id="err_active_role_stelle_eplan_email_address"
                        >{{ validators.active_role_stelle_eplan_email_address.msg.value }}</ktg-field-error
                    >
                </ktg-field>
                <ktg-clues class="mt-1rem" appearance="danger" v-if="emailerror !== ''">
                    <ktg-clue>
                        <ktg-clue-title icon="warning">{{ emailerror }}</ktg-clue-title>
                    </ktg-clue>
                </ktg-clues>
            </div>
        </div>
        <div class="right">
            <UserAccountInfo />
        </div>
    </div>
</template>

<style scoped lang="scss">
.right {
    width: 560px;
    min-width: 560px;
}

.useraccountprofilecontainer {
    background-color: var(--ktg-color-neutral-01);

    .element {
        background-color: white;
    }
}
</style>
